// src/utils/csvUtilities.ts - Following existing parseCSV and validation patterns

import Papa from 'papaparse';
import logger from './logger';

// Following the same interface pattern from existing csvUtils
export interface CsvParseResult {
  headers: string[];
  data: any[];
}

export interface LeadFieldDefinition {
  name: string;
  label: string;
  value: string;
  required: boolean;
  isRequired: boolean;
  similarNames?: string[];
  type?: string;
}

export interface FieldValidationResult {
  validCount: number;
  invalidCount: number;
  issues: string[];
  samples: {
    valid: string[];
    invalid: string[];
  };
}

export interface DataValidationResult {
  totalRows: number;
  sampleSize: number;
  fieldResults: Record<string, FieldValidationResult>;
  overallValid: number;
  overallInvalid: number;
  canProceed: boolean;
}

interface MatchResult {
  csvHeader: string;
  crmField: string;
  confidence: number;
  matchType: 'EXACT' | 'SYNONYM' | 'HIGH_SIMILARITY' | 'PARTIAL' | 'FUZZY' | 'NO_MATCH';
}

// Following the exact parseCSV pattern from existing code
export const parseCSV = (file: File): Promise<CsvParseResult> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: function (results: any) {
        try {
          if (results.errors && results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }
          
          if (!results.data || results.data.length === 0) {
            reject(new Error('CSV file appears to be empty'));
            return;
          }
          
          const headers = Object.keys(results.data[0] || {});
          if (headers.length === 0) {
            reject(new Error('No valid headers found in CSV'));
            return;
          }
          
          resolve({
            headers: headers,
            data: results.data
          });
        } catch (error) {
          reject(error);
        }
      },
      error: function (error: Error) {
        reject(error);
      }
    });
  });
};

// Following the existing generateCSVTemplate pattern
export const generateCSVTemplate = (headers: string[], filename: string) => {
  const csvContent = headers.join(',') + '\n';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Enhanced auto-mapping following the existing validation pattern
export const LEAD_FIELD_SYNONYMS: Record<string, string[]> = {
  'name': [
    'full_name', 'customer_name', 'lead_name', 'contact_name', 'first_name', 
    'fullname', 'customername', 'leadname', 'contactname', 'firstname',
    'client_name', 'clientname', 'person_name', 'personname',
    'nombre', 'nom', 'naam' // Multi-language support
  ],
  'email': [
    'email_address', 'mail', 'e_mail', 'contact_email', 'emailaddress', 
    'emailid', 'email_id', 'emailaddr', 'e_mail_address', 'emaiid',
    'correo', 'courrier', 'posta' // Multi-language
  ],
  'phone': [
    'phone_number', 'mobile', 'contact_number', 'telephone', 'cell',
    'phonenumber', 'mobilenumber', 'contactnumber', 'tel', 'cellular',
    'telefono', 'telefon', 'numero', 'movil', 'celular'
  ],
  'company': [
    'organization', 'business', 'employer', 'workplace', 'company_name',
    'companyname', 'org', 'business_name', 'businessname', 'firm',
    'empresa', 'societe', 'bedrijf', 'unternehmen'
  ],
  'address': [
    'location', 'street_address', 'full_address', 'streetaddress', 
    'fulladdress', 'address_line_1', 'addressline1', 'addr',
    'direccion', 'adresse', 'adres'
  ],
  'city': ['town', 'locality', 'city_name', 'cityname', 'ciudad', 'ville', 'stad'],
  'state': ['province', 'region', 'state_name', 'statename', 'provincia', 'etat'],
  'country': ['nation', 'country_code', 'countrycode', 'country_name', 'pais', 'pays', 'land'],
  'website': ['url', 'web_site', 'homepage', 'domain', 'website_url', 'websiteurl', 'sitio', 'site'],
  'source': ['lead_source', 'origin', 'referral', 'leadsource', 'utm_source', 'campaign_source']
};


// 1. NORMALIZATION FUNCTION - Removes spaces, special chars, converts to lowercase
export const normalizeString = (str: string): string => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim();
};

// 2. SIMILARITY CALCULATION - Levenshtein distance based similarity
export const calculateSimilarity = (str1: string, str2: string): number => {
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 100;
  
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 100;
  
  const editDistance = levenshteinDistance(longer, shorter);
  const similarity = ((longer.length - editDistance) / longer.length) * 100;
  
  return Math.round(similarity);
};

// 3. LEVENSHTEIN DISTANCE HELPER
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // insertion
        matrix[j - 1][i] + 1,     // deletion
        matrix[j - 1][i - 1] + cost // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

// Following the existing validateMapping pattern
export const generateAutoMappings = (
  csvHeaders: string[],
  leadFields: LeadFieldDefinition[],
  storedMappings?: Record<string, string>
): Record<string, string> => {
  logger.log('🚀 STARTING ENHANCED AUTO-MAPPING');
  logger.log('=====================================');
  logger.log(`📋 CSV Headers (${csvHeaders.length}):`, csvHeaders);
  logger.log(`🎯 CRM Fields (${leadFields.length}):`, leadFields.map(f => f.name));
  
  const autoMappings: Record<string, string> = {};
  const usedHeaders: Set<string> = new Set();
  
  leadFields.forEach(leadField => {
    const fieldName = leadField.name;
    const fieldValue = leadField.value;
    
    logger.log(`\n🔍 MAPPING FIELD: "${fieldName}"`);
    
    // 1. Check stored mappings first
    if (storedMappings && storedMappings[fieldValue]) {
      const storedColumn = csvHeaders.find(h => 
        normalizeString(h) === normalizeString(storedMappings[fieldValue])
      );
      if (storedColumn && !usedHeaders.has(storedColumn)) {
        autoMappings[fieldName] = storedColumn;
        usedHeaders.add(storedColumn);
        logger.log(`✅ STORED MAPPING: "${fieldName}" → "${storedColumn}"`);
        return;
      }
    }
    
    // 2. Find best match using enhanced logic
    const bestMatch = findBestMatch(fieldName, csvHeaders, usedHeaders);
    
    if (bestMatch.confidence >= 60) { // Minimum threshold
      autoMappings[fieldName] = bestMatch.csvHeader;
      usedHeaders.add(bestMatch.csvHeader);
      logger.log(`✅ ${bestMatch.matchType}: "${fieldName}" → "${bestMatch.csvHeader}" (${bestMatch.confidence}%)`);
    } else {
      logger.log(`❌ NO SUITABLE MATCH: "${fieldName}" (best: ${bestMatch.confidence}%)`);
    }
  });
  
  logger.log('\n📊 MAPPING SUMMARY:');
  logger.log(`✅ Successfully mapped: ${Object.keys(autoMappings).length} fields`);
  logger.log(`❌ No mapping found: ${leadFields.length - Object.keys(autoMappings).length} fields`);
  logger.log('Final mappings:', autoMappings);
  
  return autoMappings;
};

// 6. BEST MATCH FINDER FUNCTION
const findBestMatch = (crmField: string, csvHeaders: string[], usedHeaders: Set<string>): MatchResult => {
  const normalizedCrmField = normalizeString(crmField);
  let bestMatch: MatchResult = {
    csvHeader: '',
    crmField,
    confidence: 0,
    matchType: 'NO_MATCH'
  };
  
  logger.log(`  🎯 Normalized CRM field: "${crmField}" → "${normalizedCrmField}"`);
  
  for (const csvHeader of csvHeaders) {
    if (usedHeaders.has(csvHeader)) continue;
    
    const normalizedHeader = normalizeString(csvHeader);
    logger.log(`    📋 Checking: "${csvHeader}" → "${normalizedHeader}"`);
    
    // 1. EXACT MATCH (after normalization)
    if (normalizedHeader === normalizedCrmField) {
      logger.log(`      ✅ EXACT MATCH: 100%`);
      return {
        csvHeader,
        crmField,
        confidence: 100,
        matchType: 'EXACT'
      };
    }
    
    // 2. SYNONYM MATCH
    const synonymMatch = checkSynonymMatch(normalizedCrmField, normalizedHeader);
    if (synonymMatch > 0) {
      logger.log(`      ✅ SYNONYM MATCH: ${synonymMatch}%`);
      if (synonymMatch > bestMatch.confidence) {
        bestMatch = {
          csvHeader,
          crmField,
          confidence: synonymMatch,
          matchType: 'SYNONYM'
        };
      }
      continue;
    }
    
    // 3. SIMILARITY MATCH
    const similarity = calculateSimilarity(normalizedHeader, normalizedCrmField);
    logger.log(`      📊 Similarity: ${similarity}%`);
    
    if (similarity >= 80) {
      if (similarity > bestMatch.confidence) {
        bestMatch = {
          csvHeader,
          crmField,
          confidence: similarity,
          matchType: 'HIGH_SIMILARITY'
        };
      }
    } else if (similarity >= 60) {
      if (similarity > bestMatch.confidence) {
        bestMatch = {
          csvHeader,
          crmField,
          confidence: similarity,
          matchType: 'FUZZY'
        };
      }
    }
    
    // 4. PARTIAL MATCH (contains)
    if (normalizedHeader.includes(normalizedCrmField) || normalizedCrmField.includes(normalizedHeader)) {
      const partialScore = Math.min(70, similarity);
      logger.log(`      🔍 PARTIAL MATCH: ${partialScore}%`);
      if (partialScore > bestMatch.confidence) {
        bestMatch = {
          csvHeader,
          crmField,
          confidence: partialScore,
          matchType: 'PARTIAL'
        };
      }
    }
  }
  
  logger.log(`  🏆 Best match: "${bestMatch.csvHeader}" (${bestMatch.confidence}% - ${bestMatch.matchType})`);
  return bestMatch;
};

// 7. ENHANCED SYNONYM MATCHING
const checkSynonymMatch = (normalizedCrmField: string, normalizedHeader: string): number => {
  const synonyms = LEAD_FIELD_SYNONYMS[normalizedCrmField] || [];
  
  for (const synonym of synonyms) {
    const normalizedSynonym = normalizeString(synonym);
    
    // Exact synonym match
    if (normalizedHeader === normalizedSynonym) {
      return 95;
    }
    
    // High similarity to synonym
    const similarity = calculateSimilarity(normalizedHeader, normalizedSynonym);
    if (similarity >= 85) {
      return Math.min(90, similarity);
    }
  }
  
  return 0;
};

// Following existing validation patterns but for lead data
export const validateLeadData = (
  csvData: any[],
  fieldMappings: Record<string, string>,
  leadFields: LeadFieldDefinition[]
): DataValidationResult => {
  logger.log('🔍 Starting validation for', csvData.length, 'rows');
  
  // Take sample of 1000 rows or all if less (as per your requirement)
  const sampleSize = Math.min(1000, csvData.length);
  const sampleData = csvData.slice(0, sampleSize);
  
  const result: DataValidationResult = {
    totalRows: csvData.length,
    sampleSize,
    fieldResults: {},
    overallValid: 0,
    overallInvalid: 0,
    canProceed: true
  };
  
  // Validate each mapped field
  Object.entries(fieldMappings).forEach(([crmField, csvColumn]) => {
    if (csvColumn && csvColumn !== "Don't import") {
      const fieldData = sampleData
        .map(row => row[csvColumn])
        .filter(val => val !== undefined && val !== null);
      
      logger.log('🔍 Validating field:', crmField, 'with', fieldData.length, 'values');
      
      // Find the lead field definition
      const leadField = leadFields.find(f => f.name === crmField);
      const isRequired = leadField?.required || leadField?.isRequired || false;
      
      let validationResult: FieldValidationResult;
      
      // Different validation based on field type
      if (crmField.toLowerCase().includes('phone')) {
        validationResult = validatePhoneNumbers(fieldData);
      } else if (crmField.toLowerCase().includes('email')) {
        validationResult = validateEmails(fieldData);
      } else if (isRequired) {
        validationResult = validateRequiredField(fieldData);
      } else {
        validationResult = validateOptionalField(fieldData);
      }
      
      result.fieldResults[crmField] = validationResult;
      
      // Check if required field has too many issues
      if (isRequired && validationResult.invalidCount > validationResult.validCount) {
        result.canProceed = false;
        logger.log('❌ Required field has too many issues:', crmField);
      }
    }
  });
  
  // Calculate overall stats
  Object.values(result.fieldResults).forEach(fieldResult => {
    result.overallValid += fieldResult.validCount;
    result.overallInvalid += fieldResult.invalidCount;
  });
  
  logger.log('📊 Validation complete:', result);
  return result;
};

// Phone validation following existing patterns
export const validatePhoneNumbers = (phoneData: string[]): FieldValidationResult => {
  const result: FieldValidationResult = {
    validCount: 0,
    invalidCount: 0,
    issues: [],
    samples: { valid: [], invalid: [] }
  };
  
  phoneData.forEach(phone => {
    if (!phone || phone.toString().trim() === '') {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push('(Empty)');
      }
      return;
    }
    
    const phoneStr = phone.toString().trim();
    
    // ENHANCED VALIDATION: Only allow digits and + at start
    // Remove ONLY spaces for length checking, but flag other special chars as invalid
    const hasInvalidChars = /[^0-9\s\+]/.test(phoneStr);
    const cleanedForLength = phoneStr.replace(/[\s]/g, ''); // Only remove spaces
    const startsWithPlus = phoneStr.startsWith('+');
    
    // Check for invalid special characters
    if (hasInvalidChars) {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push(phoneStr);
      }
      if (!result.issues.includes('Contains invalid characters (only digits, + and spaces allowed)')) {
        result.issues.push('Contains invalid characters (only digits, + and spaces allowed)');
      }
      return;
    }
    
    // Check length (10-15 digits, excluding + and spaces)
    const digitsOnly = cleanedForLength.replace(/\+/, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push(phoneStr);
      }
      if (!result.issues.includes('Invalid length (should be 10-15 digits)')) {
        result.issues.push('Invalid length (should be 10-15 digits)');
      }
      return;
    }
    
    // Check + position (only at start)
    if (phoneStr.includes('+') && !startsWithPlus) {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push(phoneStr);
      }
      if (!result.issues.includes('+ symbol can only be at the beginning')) {
        result.issues.push('+ symbol can only be at the beginning');
      }
      return;
    }
    
    // Valid phone number
    result.validCount++;
    if (result.samples.valid.length < 3) {
      result.samples.valid.push(phoneStr);
    }
  });
  
  return result;
};

// Email validation following existing patterns
export const validateEmails = (emailData: string[]): FieldValidationResult => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const result: FieldValidationResult = {
    validCount: 0,
    invalidCount: 0,
    issues: [],
    samples: { valid: [], invalid: [] }
  };
  
  emailData.forEach(email => {
    if (!email || email.toString().trim() === '') {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push('(Empty)');
      }
      return;
    }
    
    const emailStr = email.toString().trim();
    if (emailRegex.test(emailStr)) {
      result.validCount++;
      if (result.samples.valid.length < 3) {
        result.samples.valid.push(emailStr);
      }
    } else {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push(emailStr);
      }
      if (!result.issues.includes('Invalid email format')) {
        result.issues.push('Invalid email format');
      }
    }
  });
  
  return result;
};

// Required field validation
export const validateRequiredField = (fieldData: string[]): FieldValidationResult => {
  const result: FieldValidationResult = {
    validCount: 0,
    invalidCount: 0,
    issues: [],
    samples: { valid: [], invalid: [] }
  };
  
  fieldData.forEach(value => {
    if (!value || value.toString().trim() === '') {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push('(Empty)');
      }
      if (!result.issues.includes('Empty values found')) {
        result.issues.push('Empty values found');
      }
    } else if (value.toString().trim().length < 2) {
      result.invalidCount++;
      if (result.samples.invalid.length < 3) {
        result.samples.invalid.push(value.toString());
      }
      if (!result.issues.includes('Values too short (less than 2 characters)')) {
        result.issues.push('Values too short (less than 2 characters)');
      }
    } else {
      result.validCount++;
      if (result.samples.valid.length < 3) {
        result.samples.valid.push(value.toString());
      }
    }
  });
  
  return result;
};

// Optional field validation (more lenient)
export const validateOptionalField = (fieldData: string[]): FieldValidationResult => {
  const result: FieldValidationResult = {
    validCount: 0,
    invalidCount: 0,
    issues: [],
    samples: { valid: [], invalid: [] }
  };
  
  fieldData.forEach(value => {
    if (!value || value.toString().trim() === '') {
      // Empty is OK for optional fields
      result.validCount++;
      return;
    } else {
      result.validCount++;
      if (result.samples.valid.length < 3) {
        result.samples.valid.push(value.toString());
      }
    }
  });
  
  return result;
};

// Following existing pattern for storing mappings
export const storeMappingForFuture = (
  mappings: Record<string, string>,
  leadFields: LeadFieldDefinition[]
): Record<string, string> => {
  const storedMappings: Record<string, string> = {};
  
  logger.log('💾 Storing mappings for future use');
  
  // Convert name-based mappings to value-based for storage (maintaining consistency)
  Object.entries(mappings).forEach(([fieldName, csvColumn]) => {
    const leadField = leadFields.find(f => f.name === fieldName);
    if (leadField && csvColumn !== "Don't import") {
      storedMappings[leadField.value] = csvColumn;
      logger.log('💾 Stored:', leadField.value, '->', csvColumn);
    }
  });
  
  return storedMappings;
};

// Following existing validateMapping pattern
export const validateMapping = (
  fieldMapping: Record<string, string>,
  requiredFields: string[]
): { isValid: boolean; missingFields: string[] } => {
  const missingFields: string[] = [];
  
  requiredFields.forEach(field => {
    if (!fieldMapping[field] || fieldMapping[field] === "" || fieldMapping[field] === "Don't import") {
      missingFields.push(field);
    }
  });
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};